/**
 * Shared chip grid for onboarding steps. Single-select by default;
 * pass `multi` to enable multi-select with optional `max`.
 */
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export default function ChipGrid({
  testIdPrefix,
  options,
  value,
  onChange,
  multi = false,
  max,
  columns = 2,
}) {
  const values = multi ? new Set(value || []) : null;

  const toggle = (opt) => {
    if (multi) {
      const next = new Set(values);
      if (next.has(opt.value)) {
        next.delete(opt.value);
      } else {
        if (max != null && next.size >= max) return;
        next.add(opt.value);
      }
      onChange?.(Array.from(next));
    } else {
      onChange?.(opt.value);
    }
  };

  const gridCols =
    columns === 2
      ? "grid-cols-1 sm:grid-cols-2"
      : columns === 3
        ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3"
        : "grid-cols-1";

  return (
    <div className={cn("grid gap-2", gridCols)} data-testid={`${testIdPrefix}-grid`}>
      {options.map((opt) => {
        const active = multi ? values.has(opt.value) : value === opt.value;
        const testId = `${testIdPrefix}-${opt.value}`;
        return (
          <button
            key={opt.value}
            type="button"
            data-testid={testId}
            data-selected={active ? "true" : "false"}
            onClick={() => toggle(opt)}
            className={cn(
              "group flex min-h-[44px] items-center gap-2 rounded-full px-3.5 py-2 text-left text-[13px] font-semibold transition-all sm:text-[12.5px]",
              active
                ? "bg-white text-black shadow-[0_6px_20px_-8px_rgba(255,255,255,0.45)]"
                : "border border-white/12 bg-white/5 text-white/75 hover:border-white/30 hover:bg-white/10 hover:text-white",
            )}
          >
            {multi && (
              <span
                className={cn(
                  "flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border",
                  active
                    ? "border-black bg-black text-white"
                    : "border-white/30 bg-transparent",
                )}
                aria-hidden="true"
              >
                {active && <Check className="h-3 w-3" strokeWidth={3} />}
              </span>
            )}
            <span className="flex-1 truncate">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}
